import { gql } from '@apollo/client';
import { LoadingIcon, PlusIcon } from '@assets/icons';
import { Button } from '@components/button';
import { DeckardFilter, SearchAndFilter } from '@components/filter';
import { InfiniteScrollTableComponents, useGraphqlPagination } from '@components/infiniteScroll';
import { Group } from '@components/layout';
import { useModal } from '@components/modal';
import { Typography } from '@components/typography';
import {
  Channel,
  ChannelFilter,
  GetOrganizationChannelsQuery,
  useGetChannelsSearchDetailQuery,
  useGetOrganizationChannelsQuery,
  useGetOrganizationRemoteDevelopmentSessionsQuery,
  useGetOrganizationSettingsQuery,
} from '@generated/graphql';
import { Box, Stack, useTheme } from '@mui/material';
import { find } from 'lodash';
import posthog from 'posthog-js';
import { useMemo, useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { ChannelTableHeader } from './ChannelTableHeader';
import {
  ChannelTableRow,
  ChannelTableRowChannelFragment,
  ChannelTableRowSessionFragment,
} from './ChannelTableRow';
import { NewChannelModal, NewChannelModalHelpContent } from './NewChannelModal';

gql`
  query GetOrganizationRemoteDevelopmentSessions {
    listRemoteDevelopment {
      channelId
      ...ChannelTableRowSessionFragment
    }
  }
  ${ChannelTableRowSessionFragment}
`;

gql`
  query GetOrganizationChannels(
    $organizationId: String
    $filters: ChannelFilter
    $limit: Int
    $cursor: String
  ) {
    getChannels(
      organizationId: $organizationId
      filters: $filters
      limit: $limit
      cursor: $cursor
    ) {
      channelId
      ...ChannelTableRowChannelFragment
    }
  }
  ${ChannelTableRowChannelFragment}
`;

type OrganizationChannelsProps = {
  organizationId: string;
};

export const OrganizationChannels = ({ organizationId }: OrganizationChannelsProps) => {
  const { palette } = useTheme();

  const [filter, setFilter] = useState<DeckardFilter<ChannelFilter>>({
    dates: { type: 'date', value: [], display: [] },
    tags: { type: 'string', value: [], display: [] },
    texts: { type: 'string', value: [], display: [] },
    users: { type: 'exact', value: [], display: [] },
  });

  const queryFilter = useMemo(() => {
    return Object.entries(filter)
      .filter(([, value]) => value.value.length > 0)
      .reduce((acc, [key, value]) => {
        acc[key] = value.value;
        return acc;
      }, {} as ChannelFilter);
  }, [filter]);
  const { data, refetch: refetchSearchDetail } = useGetChannelsSearchDetailQuery({
    variables: { organizationId },
  });
  const searchDetail = data?.getChannelsSearchDetail;

  const {
    data: channels,
    loading: channelsLoading,
    fetchMore,
    refetch: refetchChannels,
  } = useGraphqlPagination<GetOrganizationChannelsQuery, Channel>(
    useGetOrganizationChannelsQuery({
      variables: { organizationId, limit: 30, filters: queryFilter },
    }),
    'getChannels',
    'channelId',
  );

  const { data: organizationSettingsData, loading: organizationSettingsLoading } =
    useGetOrganizationSettingsQuery({
      variables: { organizationId },
    });
  const { usage, limit } =
    (!organizationSettingsLoading &&
      find(organizationSettingsData?.getOrganizationSettings, {
        setting: 'Managed Channels',
      })) ||
    {};

  const refetch = () => {
    refetchChannels();
    void refetchSearchDetail();
  };

  const { data: sessionData } = useGetOrganizationRemoteDevelopmentSessionsQuery({
    pollInterval: 3000,
  });
  const sessions = sessionData?.listRemoteDevelopment || [];

  const tableRow = (_index, channel: Channel) => (
    <ChannelTableRow
      channel={channel}
      selectedOrganizationId={organizationId}
      session={sessions.find((session) => session.channelId === channel.channelId)}
      refetch={refetch}
    />
  );

  const { showModal: showNewChannelModal } = useModal({
    component: NewChannelModal,
    componentProps: { organizationId, refetch },
    modalProps: { title: 'New Channel', help: NewChannelModalHelpContent },
  });

  const isChannelsReachedLimit = Number(usage) === Number(limit);

  return (
    <Stack spacing={5} height="100%" overflow="hidden">
      <Stack
        flex={1}
        overflow="hidden"
        p={6}
        pb={0}
        sx={{ backgroundColor: palette.background.paper }}
        gap={4}
      >
        <Group gap={4}>
          <SearchAndFilter
            context="organization-channels"
            filter={filter}
            setFilter={setFilter}
            searchDetail={searchDetail}
            variant="secondary"
          />
          <Button
            startIcon={PlusIcon}
            onClick={() => {
              posthog.capture('user_create-channel_intent', { location: 'organization-landing' });
              showNewChannelModal();
            }}
            disabled={isChannelsReachedLimit}
          >
            New channel
          </Button>
        </Group>
        {channelsLoading || organizationSettingsLoading ? (
          <Stack
            bgcolor={palette.background.paper}
            height="100%"
            justifyContent="center"
            alignItems="center"
          >
            <LoadingIcon size={16} />
          </Stack>
        ) : channels?.length === 0 ? (
          <Box
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}
          >
            <Typography>This Organization has no Channels.</Typography>
          </Box>
        ) : (
          <TableVirtuoso
            data={channels}
            endReached={fetchMore}
            components={InfiniteScrollTableComponents}
            fixedHeaderContent={ChannelTableHeader}
            itemContent={tableRow}
            increaseViewportBy={400}
          />
        )}
      </Stack>
    </Stack>
  );
};
